import { CdkDrag, CdkDragDrop, CdkDropList } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Idea } from '@models/Sessions/Idea/Idea';
import { Session } from '@models/Sessions/Session';
import { AutosizeModule } from 'ngx-autosize';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InplaceModule } from 'primeng/inplace';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';

import {
  cloneShotControl,
  emptyShotControl,
  reCreateShotControl,
  VideoProjectFormService,
  VideoSceneFormControl,
  VideoShotFormControl,
} from '../video-project.form';
import { VideoShotComponent } from '../video-shot/video-shot.component';
import { moveItemInFormArray } from './video-scene.utils';

@Component({
  selector: 'app-video-scene',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    CdkDropList,
    CdkDrag,
    CardModule,
    InplaceModule,
    ButtonModule,
    TooltipModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    TextareaModule,
    AutosizeModule,
    VideoShotComponent,
  ],
  templateUrl: './video-scene.component.html',
  styleUrl: './video-scene.component.scss',
})
export class VideoSceneComponent {
  @Input() session: Session;
  @Input() idea: Idea;
  @Input() scene: FormGroup<VideoSceneFormControl>;

  @Output() sceneDelete = new EventEmitter();

  get style() {
    if (this.scene.invalid) {
      return { outline: '2px solid var(--p-orange-500)' };
    }
    if (!this.scene.value.id) {
      return { outline: '2px solid var(--p-sky-100)' };
    }
    return {};
  }

  constructor(
    private readonly videoProjectFormService: VideoProjectFormService,
  ) {}

  onDeleteClick() {
    this.sceneDelete.emit();
  }

  onShotDrop(event: CdkDragDrop<void>) {
    moveItemInFormArray(
      this.scene.controls.shots,
      event.previousIndex,
      event.currentIndex,
    );
  }

  onShotAddClick(shot?: FormGroup<VideoShotFormControl>) {
    this.scene.controls.shots.insert(
      shot ? this.findShotIndex(shot) + 1 : 0,
      new FormGroup<VideoShotFormControl>(emptyShotControl()),
    );
  }

  onShotCloneClick(shot: FormGroup<VideoShotFormControl>) {
    const index = this.findShotIndex(shot);
    if (index >= 0) {
      this.scene.controls.shots.insert(
        index + 1,
        new FormGroup<VideoShotFormControl>(cloneShotControl(shot)),
      );
    }
  }

  onShotDeleteClick(shot: FormGroup<VideoShotFormControl>) {
    const index = this.findShotIndex(shot);
    if (index >= 0) {
      this.scene.controls.shots.removeAt(index);
    }
  }

  onShotResetClick(shot: FormGroup<VideoShotFormControl>) {
    const initialValue = this.videoProjectFormService.getShotInitialValue(shot);
    if (!initialValue) {
      return;
    }
    const index = this.findShotIndex(shot);
    if (index >= 0) {
      this.scene.controls.shots.setControl(
        index,
        new FormGroup(reCreateShotControl(initialValue)),
      );
    }
  }

  private findShotIndex(shot: FormGroup<VideoShotFormControl>) {
    return this.scene.controls.shots.controls.indexOf(shot);
  }
}
